<template>
  <div class="shipping-information mt-5 lg:mt-10">
    <the-section :title="$t('ec-line004.shippingInformation.title')">
      <el-form ref="shipping-form" class="shipping-information__form" label-position="top">
        <el-form-item v-if="user" :label="$t('common.label.shippingAddressCard')">
          <address-info-item
            class="address-info-item--bg-disabled"
            :full-name="Utils.helper.fullName(data.name01, data.name02)"
            :postal-code="Utils.helper.postalCode(data.postal_code01, data.postal_code02)"
            :address="Utils.helper.address(data.prefecture, data.addr01, data.addr02)"
            :building-name="data.addr03"
          />
        </el-form-item>

        <el-form-item v-else :label="$t('common.label.shippingAddress')">
          <disabled-input :value="data.shipping_address" />
        </el-form-item>

        <fragment v-if="data.delivery_destination === this.Constants.DELIVERY_DESTINATION.DIFFERENCE_MEMBER && !user">
          <el-form-item :label="formLabelName">
            <el-row :gutter="16">
              <el-col :span="corpCol">
                <el-form-item class="w-full">
                  <disabled-input :value="data.name01" />
                </el-form-item>
              </el-col>
              <el-col v-if="corporateFlag || !businessAccountFlag" :span="12">
                <el-form-item class="w-full">
                  <disabled-input :value="data.name02" />
                </el-form-item>
              </el-col>
            </el-row>
          </el-form-item>

          <el-form-item :label="formLabelNameKana">
            <el-row :gutter="16">
              <el-col :span="corpCol">
                <el-form-item class="w-full">
                  <disabled-input :value="data.kana01" />
                </el-form-item>
              </el-col>
              <el-col v-if="corporateFlag || !businessAccountFlag" :span="12">
                <el-form-item class="w-full">
                  <disabled-input :value="data.kana02" />
                </el-form-item>
              </el-col>
            </el-row>
          </el-form-item>

          <el-form-item :label="$t('ec-line029.content.postalCode')">
            <el-row :gutter="6" type="flex">
              <el-col :span="5">
                <el-form-item class="w-full">
                  <disabled-input :value="data.postal_code01" />
                </el-form-item>
              </el-col>
              <el-col :span="1">
                <div class="text-center">{{ $t('common.hyphenSeparator') }}</div>
              </el-col>
              <el-col :span="6">
                <el-form-item class="w-full">
                  <disabled-input :value="data.postal_code02" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <span class="text-xs">{{ $t('ec-line029.content.postalCodeTip') }}</span>
              </el-col>
            </el-row>
          </el-form-item>

          <el-form-item :label="$t('ec-line029.content.prefectures')">
            <disabled-input :value="data.prefecture" />
          </el-form-item>

          <el-form-item :label="$t('ec-line029.content.municipalities')">
            <disabled-input :value="data.addr01" />
          </el-form-item>

          <el-form-item :label="$t('ec-line029.content.address')">
            <disabled-input :value="data.addr02" />
          </el-form-item>

          <el-form-item :label="$t('ec-line029.content.buildingApartmentName')">
            <disabled-input :value="data.addr03" />
          </el-form-item>

          <el-form-item :label="$t('ec-line029.content.phoneNumber')">
            <disabled-input :value="data.phone_number" />
          </el-form-item>
        </fragment>

        <el-form-item :label="$t('common.label.shippingMethod')">
          <disabled-input :value="data.shipping_method_text" />
        </el-form-item>
        <el-form-item :label="$t('common.label.desiredDeliveryDay')">
          <disabled-input :value="data.delivery_date_text" />
        </el-form-item>
        <template>
          <el-form-item v-if="!data.time_note_disable_flag" :label="$t('common.label.desiredDeliveryTime')">
            <disabled-input :value="data.delivery_time_text" />
          </el-form-item>
          <el-form-item
            v-if="!data.time_note_disable_flag && !businessAccountFlag"
            :label="$t('common.label.specifyDeliveryNote')"
          >
            <disabled-input :value="data.delivery_note_text" />
          </el-form-item>
          <el-form-item v-if="visible_bundled_flag" :label="$t('common.label.bundledFlag')">
            <div class="flex flex-col">
              <el-radio-group :value="data.bundled_flag" disabled>
                <el-radio class="radio-dot" :label="Constants.BUNDLED_FLAG.CANBE">
                  {{ $t('common.action.canBe') }}
                </el-radio>
                <el-radio class="radio-dot" :label="Constants.BUNDLED_FLAG.NONE">
                  {{ $t('common.action.none') }}
                </el-radio>
              </el-radio-group>
              <span v-if="isNotBundledRemoteIsland" class="text-xs leading-normal mt-1 text-red">
                {{ $t('common.isNotBundledRemoteIsland') }}
              </span>
            </div>
          </el-form-item>
        </template>
      </el-form>
    </the-section>
  </div>
</template>

<script>
import TheSection from '@/components/my-page-partials/TheSection.vue'
import DisabledInput from '@/components/DisabledInput.vue'
import AddressInfoItem from '@/components/AddressInfoItem.vue'

export default {
  components: { TheSection, DisabledInput, AddressInfoItem },
  props: {
    data: { type: Object, default: () => {} },
    isCartPeriodic: { type: Boolean, default: () => false },
    isNotBundledRemoteIsland: { type: Boolean, default: () => false },
    businessAccountFlag: { type: Boolean, default: () => false },
  },
  computed: {
    visible_bundled_flag() {
      return this.$store.getters['auth/shop'].visible_bundled_flag
    },
    corporateFlag() {
      return this.$store.getters['auth/shop']?.corporate_flag
    },
    formLabelName() {
      return this.corporateFlag || !this.businessAccountFlag
        ? this.$t('common.memberInformation.fullName')
        : this.$t('common.memberInformation.companyName')
    },
    formLabelNameKana() {
      return this.corporateFlag || !this.businessAccountFlag
        ? this.$t('common.memberInformation.nameKana')
        : this.$t('common.memberInformation.companyNameKana')
    },
    corpCol() {
      return this.corporateFlag || !this.businessAccountFlag ? 12 : 24
    },
  },
}
</script>
